import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  getJourneyPortfolio,
  saveJourneyPortfolio,
  type JourneyPortfolio,
} from './studyYearApi';

export const MAX_STORY_HIGHLIGHTS = 20;
export const MAX_STORY_NARRATIVE = 3000;

export const portfolioQueryKey = (yearId: string) => ['study-journey-portfolio', yearId] as const;

/** Saved highlight keys that still point at a record. */
export function savedHighlightKeys(saved: JourneyPortfolio | undefined): string[] {
  return saved?.highlights.filter((highlight) => highlight.available).map((h) => h.key) ?? [];
}

/**
 * The learner's year story for one academic year: the stars on My journey records and the story
 * text. Every save reuses its request id for the same body, so a retry after a lost response
 * cannot write twice.
 */
export function useJourneyStory(yearId: string) {
  const queryClient = useQueryClient();
  const keys = useRef(new Map<string, string>());
  const query = useQuery({
    queryKey: portfolioQueryKey(yearId),
    queryFn: () => getJourneyPortfolio(yearId),
    enabled: Boolean(yearId),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (input: { narrative: string; highlights: string[] }) => {
      if (!query.data) throw new Error('Story not loaded');
      const body = {
        academic_year_id: yearId,
        expected_version: query.data.version,
        narrative: input.narrative,
        highlights: input.highlights,
      };
      const fingerprint = JSON.stringify(body);
      let requestId = keys.current.get(fingerprint);
      if (!requestId) {
        requestId = crypto.randomUUID();
        keys.current.set(fingerprint, requestId);
      }
      return saveJourneyPortfolio({ ...body, request_id: requestId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portfolioQueryKey(yearId) });
    },
  });
  const starred = new Set(savedHighlightKeys(query.data));
  const toggleStar = (key: string) => {
    if (!query.data) return;
    const next = starred.has(key)
      ? [...starred].filter((saved) => saved !== key)
      : [...starred, key];
    if (next.length > MAX_STORY_HIGHLIGHTS) return;
    save.mutate({ narrative: query.data.narrative, highlights: next });
  };
  return { query, save, starred, toggleStar };
}

export type JourneyStory = ReturnType<typeof useJourneyStory>;
